import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { addAllowance, updateAllowance } from "../../redux/allowanceSlice";
import { fetchEmployees } from "../../redux/employeeSlice";

const AllowancedetailModal = ({ isOpen, onClose, initialData }) => {
  const dispatch = useDispatch();
  const { employees, status: employeeStatus } = useSelector((state) => state.employees || { employees: [], status: "idle" });
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();
  const [formError, setFormError] = useState(null);

  // Fetch employees for dropdown
  useEffect(() => {
    if (employeeStatus === "idle") {
      dispatch(fetchEmployees());
    }
  }, [dispatch, employeeStatus]);

  // Set form values for editing or reset for new allowance
  useEffect(() => {
    if (initialData) {
      Object.keys(initialData).forEach((key) => {
        if (key === "applyDate") {
          const dateValue = initialData[key] ? new Date(initialData[key]).toISOString().split("T")[0] : "";
          setValue(key, dateValue);
        } else {
          setValue(key, initialData[key] || "");
        }
      });
    } else {
      reset();
      setValue("sessionName", "March-2024");
    }
  }, [initialData, setValue, reset]);

  const handleFormSubmit = async (data) => {
    try {
      const selectedEmployee = employees.find((emp) => `${emp.firstName} ${emp.lastName}` === data.employeeName);
      if (!selectedEmployee) {
        throw new Error("Please select a valid employee");
      }

      const allowanceData = {
        employeeId: selectedEmployee.id,
        employeeCode: data.employeeCode || "SAS-015",
        employeeName: data.employeeName,
        applyDate: new Date(data.applyDate).toISOString(),
        salary: parseFloat(data.salary),
        sessionName: data.sessionName,
        duesType: data.duesType,
        duesAmount: parseFloat(data.duesAmount),
        description: data.description || null,
        status: initialData?.status || "Pending",
      };

      if (initialData) {
        await dispatch(updateAllowance({ id: initialData.id, updatedData: allowanceData })).unwrap();
      } else {
        await dispatch(addAllowance(allowanceData)).unwrap();
      }

      reset();
      setFormError(null);
      onClose();
    } catch (err) {
      setFormError(err.message || "Failed to save allowance");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-medium">
            {initialData ? "Edit Allowance for Employee" : "Apply Allowance to Employee"}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-4 space-y-4">
          {formError && <p className="text-red-500 text-sm">{formError}</p>}
          <div>
            <label className="block text-sm font-medium">Session Name</label>
            <input
              type="text"
              className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
              {...register("sessionName")}
              readOnly
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Apply Date *</label>
            <input
              type="date"
              className={`mt-1 w-full border ${errors.applyDate ? "border-red-500" : "border-gray-300"} rounded px-3 py-2`}
              {...register("applyDate", { required: "Apply date is required" })}
            />
            {errors.applyDate && <p className="text-red-500 text-sm">{errors.applyDate.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium">Salary *</label>
            <input
              type="number"
              className={`mt-1 w-full border ${errors.salary ? "border-red-500" : "border-gray-300"} rounded px-3 py-2`}
              {...register("salary", { 
                required: "Salary is required",
                min: { value: 0, message: "Salary must be positive" }
              })}
            />
            {errors.salary && <p className="text-red-500 text-sm">{errors.salary.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium">Employee Name *</label>
            <select
              className={`mt-1 w-full border ${errors.employeeName ? "border-red-500" : "border-gray-300"} rounded px-3 py-2`}
              {...register("employeeName", { required: "Employee name is required" })}
            >
              <option value="" disabled>
                Select option
              </option>
              {employeeStatus === "loading" && <option disabled>Loading employees...</option>}
              {employeeStatus === "succeeded" && employees.map((employee) => (
                <option key={employee.id} value={`${employee.firstName} ${employee.lastName}`}>
                  {employee.firstName} {employee.lastName}
                </option>
              ))}
            </select>
            {errors.employeeName && <p className="text-red-500 text-sm">{errors.employeeName.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium">Allowance Type *</label>
            <select
              className={`mt-1 w-full border ${errors.duesType ? "border-red-500" : "border-gray-300"} rounded px-3 py-2`}
              {...register("duesType", { required: "Allowance type is required" })}
            >
              <option value="" disabled>
                Select Type
              </option>
              <option value="Welfare">Welfare</option>
              <option value="Funeral">Funeral</option>
            </select>
            {errors.duesType && <p className="text-red-500 text-sm">{errors.duesType.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium">Amount *</label>
            <input
              type="number"
              className={`mt-1 w-full border ${errors.duesAmount ? "border-red-500" : "border-gray-300"} rounded px-3 py-2`}
              {...register("duesAmount", { 
                required: "Amount is required",
                min: { value: 0, message: "Amount must be positive" }
              })}
            />
            {errors.duesAmount && <p className="text-red-500 text-sm">{errors.duesAmount.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium">Description</label>
            <textarea
              className="mt-1 w-full border border-gray-300 rounded px-3 py-2"
              {...register("description")}
              placeholder="Enter description"
            ></textarea>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-[#231763] text-white px-4 py-2 rounded"
              disabled={employeeStatus === "loading"}
            >
              {initialData ? "Update Allowance" : "Apply Allowance"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AllowancedetailModal;